import { useEffect, useState } from "react";

import { t } from "../../i18n/i18n";
import { IntroSketch } from "./IntroSketch";

const SEEN_KEY = "generex.intro-seen";
export const DURATION_MS = 8000;

// Fin de l'animation attendue par la prise en main et l'annonce des retours, qui ne s'ouvrent jamais par-dessus
// (demande d'Alexis du 26/10/2026).
let introRunning = false;
const introWaiters = new Set<() => void>();

/** Appelle `run` dès que l'animation d'ouverture est finie (tout de suite si elle ne se joue pas) ; rend de quoi
 *  annuler l'attente. */
export function whenIntroDone(run: () => void): () => void {
  if (!introRunning) {
    run();
    return () => undefined;
  }
  introWaiters.add(run);
  return () => introWaiters.delete(run);
}

function introFinished(): void {
  introRunning = false;
  const waiting = [...introWaiters];
  introWaiters.clear();
  for (const run of waiting) run();
}

/** Une seule fois par session de l'onglet ; jamais si le système demande moins d'animations. */
function shouldPlay(): boolean {
  try {
    if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) return false;
    if (window.sessionStorage.getItem(SEEN_KEY)) return false;
    window.sessionStorage.setItem(SEEN_KEY, "1");
  } catch {
    // Stockage refusé (navigation privée) : l'animation se joue, sans mémoire.
  }
  return true;
}

/** Animation d'ouverture de l'accueil (08/10/2026) : logo du flou au net avec halo, nom qui monte en place, puis
 *  accroche, croquis tactique sous l'accroche (IntroSketch, 26/10/2026, joué dans le même temps), fondu vers l'accueil
 *  (8 s). Un clic la passe. Elle recouvre l'accueil sans toucher au logo de son en-tête, qui reste
 *  immobile entre l'application et l'accueil. */
export function IntroAnimation() {
  const [playing, setPlaying] = useState(() => {
    const play = shouldPlay();
    introRunning = play;
    return play;
  });
  useEffect(() => {
    if (!playing) {
      if (introRunning) introFinished();
      return;
    }
    const timer = window.setTimeout(() => setPlaying(false), DURATION_MS);
    return () => window.clearTimeout(timer);
  }, [playing]);
  if (!playing) return null;
  // ui-check: ignore-start — écran de marque plein cadre : coins et graisse du logo (comme GenerexMark), titre
  // agrandi, voile opaque qui recouvre l'accueil le temps de l'animation.
  return (
    <div
      className="intro-overlay fixed inset-0 z-[80] grid cursor-pointer place-items-center bg-canvas"
      onClick={() => setPlaying(false)}
      role="presentation"
      aria-hidden
      title={t("Cliquez pour passer")}
    >
      <div className="relative flex flex-col items-center gap-5">
        <div className="relative grid place-items-center">
          <span className="intro-halo absolute size-40 rounded-full bg-accent/30 blur-2xl" />
          <img
            src="/generex-icon.png"
            width={104}
            height={104}
            alt=""
            draggable={false}
            className="intro-mark relative rounded-[22.5%] shadow-lg"
          />
        </div>
        <span className="intro-name flex items-baseline gap-2 text-[20px] font-bold">
          <span className="text-ink">GENEREX</span>
          <span className="tracking-normal text-ink-muted">Web</span>
        </span>
        <span className="intro-tagline max-w-[34rem] px-6 text-center text-[17px] leading-snug font-medium text-ink-soft">
          {t("Gagnez du temps dans la conception, la production et l'exploitation des ordres")}
        </span>
        {/* Croquis tactique sous l'accroche, hors du flux : le bloc centré (logo, nom, accroche) ne bouge pas. */}
        <IntroSketch className="absolute top-full left-1/2 mt-10 -translate-x-1/2" />
      </div>
    </div>
  );
  // ui-check: ignore-end
}
